import { createRootRoute, createRoute, createRouter, Link, Outlet } from '@tanstack/react-router';
import { AuthLayout } from '@/features/auth/ui/AuthLayout';
import { ForgotPasswordPage } from '@/features/auth/ui/ForgotPasswordPage';
import { LoginPage } from '@/features/auth/ui/LoginPage';
import { RegisterPage } from '@/features/auth/ui/RegisterPage';
import { ResetPasswordPage } from '@/features/auth/ui/ResetPasswordPage';
import { SecuritySettingsPage } from '@/features/auth/ui/SecuritySettingsPage';
import { VerifyEmailPage } from '@/features/auth/ui/VerifyEmailPage';
import { DashboardPage } from '@/features/dashboard/ui/DashboardPage';
import { PrivacyPage } from '@/features/legal/ui/PrivacyPage';
import { TermsPage } from '@/features/legal/ui/TermsPage';
import { AcceptInvitationPage } from '@/features/organizations/ui/AcceptInvitationPage';
import { CreateOrganizationPage } from '@/features/organizations/ui/CreateOrganizationPage';
import { MembersPage } from '@/features/organizations/ui/MembersPage';
import { OrgSettingsPage } from '@/features/organizations/ui/OrgSettingsPage';
import { AgentsPage } from '@/features/agents/ui/AgentsPage';
import { AiSettingsPage } from '@/features/ai/ui/AiSettingsPage';
import { ProjectDetailPage } from '@/features/projects/ui/ProjectDetailPage';
import { ProjectsListPage } from '@/features/projects/ui/ProjectsListPage';
import { AuthenticatedLayout } from './AuthenticatedLayout';
import { PublicOnly } from './guards';

const rootRoute = createRootRoute({ component: () => <Outlet /> });

/* --- Protected application area ------------------------------------------ */
const appLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  component: AuthenticatedLayout,
});

const dashboardRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/',
  component: DashboardPage,
});

const securityRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings/security',
  component: SecuritySettingsPage,
});

const membersRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/members',
  component: MembersPage,
});

const createOrgRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/organizations/new',
  component: CreateOrganizationPage,
});

const projectsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/projects',
  component: ProjectsListPage,
});

const projectDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/projects/$projectId',
  component: ProjectDetailPage,
});

const aiSettingsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings/ai',
  component: AiSettingsPage,
});

const orgSettingsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings/organization',
  component: OrgSettingsPage,
});

const agentsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/agents',
  component: AgentsPage,
});

/* --- Public auth screens -------------------------------------------------- */
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: () => (
    <PublicOnly>
      <AuthLayout
        title="Sign in"
        subtitle="Welcome back."
        footer={<Link to="/register">Create an account</Link>}
      >
        <LoginPage />
      </AuthLayout>
    </PublicOnly>
  ),
});

const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/register',
  component: () => (
    <PublicOnly>
      <AuthLayout
        title="Create account"
        subtitle="Start automating your marketing."
        footer={<Link to="/login">I already have an account</Link>}
      >
        <RegisterPage />
      </AuthLayout>
    </PublicOnly>
  ),
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: () => (
    <PublicOnly>
      <AuthLayout
        title="Forgot password"
        subtitle="We'll email you a reset link."
        footer={<Link to="/login">Back to sign in</Link>}
      >
        <ForgotPasswordPage />
      </AuthLayout>
    </PublicOnly>
  ),
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: () => (
    <PublicOnly>
      <AuthLayout title="Reset password">
        <ResetPasswordPage />
      </AuthLayout>
    </PublicOnly>
  ),
});

const verifyEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/verify-email',
  component: () => (
    <AuthLayout title="Email verification">
      <VerifyEmailPage />
    </AuthLayout>
  ),
});

// Accepting an invitation works signed-in or not (it prompts for sign-in), so it
// lives outside the protected app layout.
const acceptInvitationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invitations/accept',
  component: AcceptInvitationPage,
});

// Public legal pages (used for the Terms/Privacy URLs required by the
// social-platform app reviews, and reachable by anyone).
const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/terms',
  component: TermsPage,
});

const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/privacy',
  component: PrivacyPage,
});

const routeTree = rootRoute.addChildren([
  appLayoutRoute.addChildren([
    dashboardRoute,
    securityRoute,
    membersRoute,
    createOrgRoute,
    projectsRoute,
    projectDetailRoute,
    aiSettingsRoute,
    orgSettingsRoute,
    agentsRoute,
  ]),
  loginRoute,
  registerRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  verifyEmailRoute,
  acceptInvitationRoute,
  termsRoute,
  privacyRoute,
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
